// ============================================
// Patient-Facing Product Detail Page
// Shows full supplement info: description, dosing,
// evidence, biomarkers, safety, and CTA to upload labs
// ============================================

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getProduct } from '../../services/firestore/formularyService';
import { useApp } from '../../context/AppContext';
import type { Product } from '../../types/formulary';
import type { Protocol, SupplementRecommendation, ProtocolTiming } from '../../types/index';

// ── Display helpers ──

const TIMING_LABELS: Record<ProtocolTiming, string> = {
  AM_ENERGY: 'Morning',
  MIDDAY_ABSORPTION: 'Midday',
  PM_RECOVERY: 'Evening',
};

const TIMING_DESCRIPTIONS: Record<ProtocolTiming, string> = {
  AM_ENERGY: 'Take in the morning with breakfast for optimal energy support and absorption.',
  MIDDAY_ABSORPTION: 'Take midday, ideally with a meal, for sustained absorption throughout the day.',
  PM_RECOVERY: 'Take in the evening to support overnight recovery and restful sleep.',
};

const TIMING_COLORS: Record<ProtocolTiming, { bg: string; text: string; border: string }> = {
  AM_ENERGY: { bg: '#FFFBEB', text: '#B45309', border: '#FDE68A' },
  MIDDAY_ABSORPTION: { bg: '#F0F9FF', text: '#0369A1', border: '#BAE6FD' },
  PM_RECOVERY: { bg: '#EEF2FF', text: '#4338CA', border: '#C7D2FE' },
};

const EVIDENCE_DESCRIPTIONS: Record<string, string> = {
  strong: 'Supported by multiple randomized controlled trials, meta-analyses, or systematic reviews with consistent results across diverse populations.',
  moderate: 'Supported by well-designed clinical studies with positive outcomes, though additional research may further clarify optimal dosing or populations.',
  emerging: 'Preliminary evidence from pilot studies, observational data, or mechanistic research. Promising but requires additional clinical validation.',
};

const SUBCATEGORY_LABELS: Record<string, string> = {
  foundation: 'Foundation',
  beauty: 'Beauty',
  weight_metabolic: 'Weight & Metabolic',
  hormone_female: 'Hormone (Female)',
  hormone_male: 'Hormone (Male)',
  clinical: 'Clinical',
  hrt_female: 'HRT (Female)',
  hrt_male: 'HRT (Male)',
  thyroid: 'Thyroid',
  glp1_weight: 'GLP-1 Weight',
  skin_hair: 'Skin & Hair',
  mental_health: 'Mental Health',
  sexual_female: 'Sexual Health (Female)',
  sexual_male: 'Sexual Health (Male)',
  lipid: 'Lipid',
  nad_cellular: 'NAD+ & Cellular',
  gh_peptide: 'GH Peptide',
  mtor_longevity: 'mTOR & Longevity',
  metabolic_longevity: 'Metabolic Longevity',
  immune_inflammation: 'Immune & Inflammation',
  mitochondrial_cognitive: 'Mitochondrial & Cognitive',
  neurohormone: 'Neurohormone',
  hormone_precursor: 'Hormone Precursor',
  methylation_energy: 'Methylation & Energy',
  antioxidant_detox: 'Antioxidant & Detox',
  joint_musculoskeletal: 'Joint & Musculoskeletal',
  skin_regeneration: 'Skin Regeneration',
};

export function ProductDetail() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { dispatch } = useApp();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cartEmail, setCartEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailError, setEmailError] = useState('');

  const handleAddToCart = (p: Product) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!cartEmail.trim() || !emailRegex.test(cartEmail.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }

    const supplement: SupplementRecommendation = {
      productId: p.id,
      name: p.name,
      dosage: p.dosage,
      timing: p.timing,
      rationale: p.clinicalNotes || p.rationale || '',
      targetBiomarker: p.targetBiomarkers?.[0] || '',
      gender: p.gender === 'female' ? 'F' : p.gender === 'male' ? 'M' : 'both',
      contraindications: p.contraindications || [],
      interactionWarnings: p.drugInteractions || [],
      evidenceLevel: p.evidenceLevel,
      requiresRx: p.requiresRx,
    };

    const protocol: Protocol = {
      id: `catalog-${p.id}-${Date.now()}`,
      createdAt: new Date().toISOString(),
      supplements: [supplement],
      summary: `Direct purchase: ${p.name}`,
      tierRecommendation: 1,
      warnings: [],
      bundleName: p.name,
      bundlePrice: p.pricing?.monthlyPrice || '',
    };

    dispatch({ type: 'SET_PROTOCOL', payload: protocol });
    dispatch({ type: 'SET_CHECKOUT_EMAIL', payload: cartEmail.trim() });
    navigate('/checkout');
  };

  useEffect(() => {
    async function load() {
      if (!productId) {
        setError('No product specified.');
        setLoading(false);
        return;
      }
      try {
        const data = await getProduct(productId);
        if (!data || data.status !== 'active') {
          setError('Product not found.');
        } else {
          setProduct(data);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load product');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [productId]);

  // ── Loading ──
  if (loading) {
    return (
      <div style={{
        backgroundColor: '#FAFAFA', minHeight: '100vh',
        fontFamily: "'DM Sans', system-ui, sans-serif",
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          border: '3px solid #E4E4E7', borderTopColor: '#E11D48',
          animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  // ── Error / Not found ──
  if (error || !product) {
    return (
      <div style={{
        backgroundColor: '#FAFAFA', minHeight: '100vh',
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}>
        <DetailNav />
        <div style={{
          maxWidth: 640, margin: '0 auto', padding: '80px 24px',
          textAlign: 'center',
        }}>
          <h1 style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontSize: 32, fontWeight: 400, color: '#18181B',
            marginBottom: 16,
          }}>
            {error || 'Product not found'}
          </h1>
          <Link to="/catalog" style={{
            fontSize: 15, color: '#E11D48', fontWeight: 500,
            textDecoration: 'none',
          }}>
            Back to catalog
          </Link>
        </div>
      </div>
    );
  }

  const timing = TIMING_COLORS[product.timing] || { bg: '#F4F4F5', text: '#52525B', border: '#E4E4E7' };
  const allTimings = [product.timing, ...(product.additionalTimings || [])];

  return (
    <div style={{ backgroundColor: '#FAFAFA', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <DetailNav />

      <div style={{ maxWidth: 880, margin: '0 auto', padding: '32px 24px 80px' }}>
        {/* Back link */}
        <Link
          to="/catalog"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 14, color: '#71717A', textDecoration: 'none',
            marginBottom: 32, transition: 'color 0.2s',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#E11D48'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#71717A'; }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
          Back to catalog
        </Link>

        {/* Product header */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <span style={{
              display: 'inline-block', padding: '4px 12px',
              fontSize: 12, fontWeight: 600, borderRadius: 3,
              backgroundColor: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0',
              letterSpacing: 0.3,
            }}>
              {SUBCATEGORY_LABELS[product.subcategory] || product.subcategory}
            </span>
            <span style={{
              display: 'inline-block', padding: '4px 12px',
              fontSize: 12, fontWeight: 600, borderRadius: 3,
              backgroundColor: '#F4F4F5', color: '#52525B', border: '1px solid #E4E4E7',
              textTransform: 'capitalize', letterSpacing: 0.3,
            }}>
              Tier {product.tier}
            </span>
            {product.requiresRx && (
              <span style={{
                display: 'inline-block', padding: '4px 12px',
                fontSize: 12, fontWeight: 600, borderRadius: 3,
                backgroundColor: '#FFF1F2', color: '#BE123C', border: '1px solid #FECDD3',
                letterSpacing: 0.3,
              }}>
                Prescription Required
              </span>
            )}
            {product.gender !== 'any' && (
              <span style={{
                display: 'inline-block', padding: '4px 12px',
                fontSize: 12, fontWeight: 600, borderRadius: 3,
                backgroundColor: '#F5F3FF', color: '#6D28D9', border: '1px solid #DDD6FE',
                letterSpacing: 0.3, textTransform: 'capitalize',
              }}>
                {product.gender} only
              </span>
            )}
          </div>

          <h1 style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontSize: 'clamp(28px, 5vw, 42px)',
            fontWeight: 400, color: '#18181B',
            margin: '0 0 16px', lineHeight: 1.15,
          }}>
            {product.name}
          </h1>

          {product.ingredients && (
            <p style={{
              fontSize: 15, color: '#71717A', margin: '0 0 8px',
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {product.ingredients}
            </p>
          )}
        </div>

        {/* Two-column layout */}
        <div className="product-detail-grid">
          {/* Left column: details */}
          <div>
            {/* Clinical Notes */}
            {product.clinicalNotes && (
              <Section title="About This Supplement">
                <p style={{ fontSize: 15, color: '#52525B', lineHeight: 1.7, margin: 0 }}>
                  {product.clinicalNotes}
                </p>
              </Section>
            )}

            {/* Rationale */}
            {product.rationale && product.rationale !== product.clinicalNotes && (
              <Section title="How It Works">
                <p style={{ fontSize: 15, color: '#52525B', lineHeight: 1.7, margin: 0 }}>
                  {product.rationale}
                </p>
              </Section>
            )}

            {/* Dosage & Timing */}
            <Section title="Dosage & Timing">
              <div style={{
                backgroundColor: timing.bg, border: `1px solid ${timing.border}`,
                borderRadius: 4, padding: 20, marginBottom: 16,
              }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: timing.text,
                  letterSpacing: 0.5, marginBottom: 8,
                  textTransform: 'uppercase',
                }}>
                  {allTimings.map((t) => TIMING_LABELS[t] || t).join(' + ')}
                </div>
                <div style={{
                  fontSize: 17, fontWeight: 600, color: '#18181B',
                  fontFamily: "'JetBrains Mono', monospace",
                  marginBottom: 8,
                }}>
                  {product.dosage}
                </div>
                <p style={{ fontSize: 14, color: '#52525B', margin: 0, lineHeight: 1.5 }}>
                  {TIMING_DESCRIPTIONS[product.timing] || ''}
                </p>
              </div>
              <div style={{
                fontSize: 13, color: '#71717A', fontStyle: 'italic',
              }}>
                Route: {product.route}
              </div>
            </Section>

            {/* Target Biomarkers */}
            {product.targetBiomarkers && product.targetBiomarkers.length > 0 && (
              <Section title="Target Biomarkers">
                <p style={{ fontSize: 14, color: '#71717A', marginBottom: 14, marginTop: 0 }}>
                  This supplement is mapped to the following lab markers. Upload your bloodwork to see how your levels compare.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {product.targetBiomarkers.map((b) => (
                    <span key={b} style={{
                      display: 'inline-block', padding: '6px 14px',
                      fontSize: 13, fontWeight: 500, borderRadius: 4,
                      backgroundColor: '#F0FDF4', color: '#15803D',
                      border: '1px solid #BBF7D0',
                      fontFamily: "'JetBrains Mono', monospace",
                    }}>
                      {b}
                    </span>
                  ))}
                </div>
              </Section>
            )}

            {/* Triggers */}
            {product.triggers && product.triggers.length > 0 && (
              <Section title="When This Gets Recommended">
                <div style={{
                  backgroundColor: '#FFFFFF', border: '1px solid #E4E4E7',
                  borderRadius: 4, overflow: 'hidden',
                }}>
                  {/* Table header */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr auto',
                    padding: '12px 16px',
                    backgroundColor: '#18181B', color: '#FFFFFF',
                    fontSize: 12, fontWeight: 600, letterSpacing: 0.5,
                    textTransform: 'uppercase',
                  }}>
                    <span>Biomarker</span>
                    <span>Condition</span>
                    <span>Threshold</span>
                  </div>
                  {product.triggers.map((trigger, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr auto',
                        padding: '12px 16px',
                        backgroundColor: i % 2 === 0 ? '#FFFFFF' : '#FAFAFA',
                        borderTop: '1px solid #F4F4F5',
                        fontSize: 14, color: '#52525B',
                      }}
                    >
                      <span style={{ fontWeight: 500, color: '#18181B' }}>{trigger.biomarker}</span>
                      <span style={{ textTransform: 'capitalize' }}>
                        {trigger.condition.replace(/_/g, ' ')}
                      </span>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>
                        {trigger.description || (trigger.threshold !== undefined ? String(trigger.threshold) : '--')}
                      </span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Evidence */}
            <Section title="Evidence Level">
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
              }}>
                <EvidenceBadge level={product.evidenceLevel} />
                <span style={{
                  fontSize: 16, fontWeight: 600, color: '#18181B',
                  textTransform: 'capitalize',
                }}>
                  {product.evidenceLevel} Evidence
                </span>
              </div>
              <p style={{
                fontSize: 14, color: '#52525B', lineHeight: 1.65, margin: 0,
              }}>
                {EVIDENCE_DESCRIPTIONS[product.evidenceLevel] || ''}
              </p>
              {product.evidenceSummary && (
                <p style={{
                  fontSize: 14, color: '#52525B', lineHeight: 1.65,
                  marginTop: 12, marginBottom: 0,
                  paddingTop: 12, borderTop: '1px solid #F4F4F5',
                }}>
                  {product.evidenceSummary}
                </p>
              )}
              {product.citations && product.citations.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{
                    fontSize: 12, fontWeight: 600, color: '#A1A1AA',
                    letterSpacing: 1.5, textTransform: 'uppercase',
                    marginBottom: 8,
                  }}>
                    References
                  </div>
                  <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
                    {product.citations.map((c, i) => (
                      <li key={i} style={{
                        fontSize: 13, color: '#71717A', lineHeight: 1.5,
                        marginBottom: 4,
                      }}>
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Section>

            {/* Safety */}
            {(product.contraindications?.length > 0 || product.drugInteractions?.length > 0 || product.allergenFlags?.length > 0) && (
              <Section title="Safety Information">
                {product.contraindications?.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <h4 style={{
                      fontSize: 14, fontWeight: 600, color: '#DC2626',
                      margin: '0 0 8px',
                    }}>
                      Contraindications
                    </h4>
                    <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
                      {product.contraindications.map((c, i) => (
                        <li key={i} style={{
                          fontSize: 14, color: '#52525B', lineHeight: 1.5,
                          marginBottom: 4,
                        }}>
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {product.drugInteractions?.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <h4 style={{
                      fontSize: 14, fontWeight: 600, color: '#B45309',
                      margin: '0 0 8px',
                    }}>
                      Drug Interactions
                    </h4>
                    <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
                      {product.drugInteractions.map((d, i) => (
                        <li key={i} style={{
                          fontSize: 14, color: '#52525B', lineHeight: 1.5,
                          marginBottom: 4,
                        }}>
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {product.allergenFlags?.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <h4 style={{
                      fontSize: 14, fontWeight: 600, color: '#52525B',
                      margin: '0 0 8px',
                    }}>
                      Allergen Flags
                    </h4>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {product.allergenFlags.map((a, i) => (
                        <span key={i} style={{
                          display: 'inline-block', padding: '4px 10px',
                          fontSize: 12, fontWeight: 600, borderRadius: 3,
                          backgroundColor: '#FEF3C7', color: '#92400E',
                          border: '1px solid #FDE68A',
                        }}>
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {product.pregnancyCategory && product.pregnancyCategory !== 'N/A' && (
                  <div style={{
                    fontSize: 13, color: '#71717A', paddingTop: 12,
                    borderTop: '1px solid #F4F4F5',
                  }}>
                    Pregnancy Category: <strong style={{ color: '#52525B' }}>{product.pregnancyCategory}</strong>
                  </div>
                )}
              </Section>
            )}

            {/* Monitoring */}
            {product.monitoringRequirements?.length > 0 && (
              <Section title="Monitoring Requirements">
                <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
                  {product.monitoringRequirements.map((m, i) => (
                    <li key={i} style={{
                      fontSize: 14, color: '#52525B', lineHeight: 1.6,
                      marginBottom: 6,
                    }}>
                      {m}
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </div>

          {/* Right column: sticky sidebar */}
          <div style={{ position: 'sticky', top: 88 }}>
            {/* CTA Card */}
            <div style={{
              backgroundColor: '#FFFFFF', border: '1px solid #E4E4E7',
              borderRadius: 4, padding: 28,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}>
              {/* Price */}
              {product.pricing?.monthlyPrice && (
                <div style={{ marginBottom: 20 }}>
                  <span style={{
                    fontSize: 28, fontWeight: 700, color: '#18181B',
                  }}>
                    {product.pricing.monthlyPrice}
                  </span>
                  <span style={{ fontSize: 15, color: '#71717A', marginLeft: 4 }}>/mo</span>
                </div>
              )}

              {/* Suggested Use */}
              <div style={{
                fontSize: 13, color: '#52525B', lineHeight: 1.5,
                marginBottom: 20, padding: '12px 14px',
                backgroundColor: '#FAFAFA', borderRadius: 4,
                border: '1px solid #F4F4F5',
              }}>
                <span style={{ fontWeight: 600, color: '#18181B', display: 'block', marginBottom: 4, fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase' as const }}>
                  Suggested Use
                </span>
                {product.dosage}
              </div>

              {/* Add to Cart flow — only for non-Rx supplements */}
              {!product.requiresRx && !product.requiresProvider ? (
                <>
                  {!showEmailInput ? (
                    <button
                      onClick={() => setShowEmailInput(true)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'center',
                        padding: '14px 28px', fontSize: 15, fontWeight: 600,
                        backgroundColor: '#E11D48', color: '#FFFFFF',
                        border: 'none', borderRadius: 4,
                        cursor: 'pointer',
                        transition: 'background-color 0.25s',
                        marginBottom: 12,
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#BE123C'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#E11D48'; }}
                    >
                      Add to Cart
                    </button>
                  ) : (
                    <div style={{ marginBottom: 12 }}>
                      <label style={{
                        display: 'block', fontSize: 13, fontWeight: 500,
                        color: '#52525B', marginBottom: 6,
                      }}>
                        Email for order confirmation
                      </label>
                      <input
                        type="email"
                        placeholder="you@example.com"
                        value={cartEmail}
                        onChange={(e) => { setCartEmail(e.target.value); setEmailError(''); }}
                        style={{
                          width: '100%', padding: '12px 14px',
                          fontSize: 15, fontFamily: "'DM Sans', sans-serif",
                          border: `1px solid ${emailError ? '#DC2626' : '#E4E4E7'}`,
                          borderRadius: 4, backgroundColor: '#FAFAFA',
                          color: '#18181B', outline: 'none',
                          marginBottom: 4, boxSizing: 'border-box',
                        }}
                        onFocus={(e) => { e.target.style.borderColor = '#E11D48'; }}
                        onBlur={(e) => { if (!emailError) e.target.style.borderColor = '#E4E4E7'; }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddToCart(product); }}
                      />
                      {emailError && (
                        <p style={{ fontSize: 12, color: '#DC2626', margin: '0 0 8px' }}>{emailError}</p>
                      )}
                      <button
                        onClick={() => handleAddToCart(product)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'center',
                          padding: '14px 28px', fontSize: 15, fontWeight: 600,
                          backgroundColor: '#E11D48', color: '#FFFFFF',
                          border: 'none', borderRadius: 4,
                          cursor: 'pointer', marginTop: 8,
                          transition: 'background-color 0.25s',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#BE123C'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#E11D48'; }}
                      >
                        Proceed to Checkout
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div style={{
                  padding: '12px 16px', backgroundColor: '#FFF1F2',
                  borderRadius: 4, border: '1px solid #FECDD3',
                  fontSize: 13, color: '#BE123C', lineHeight: 1.5,
                  marginBottom: 12,
                }}>
                  {product.requiresRx ? 'This product requires a prescription.' : 'Provider consultation required.'}
                  {' '}Upload your labs to get started.
                </div>
              )}

              <Link
                to="/landing"
                style={{
                  display: 'block', textAlign: 'center',
                  padding: '12px 28px', fontSize: 14, fontWeight: 500,
                  color: '#52525B', border: '1.5px solid #E4E4E7',
                  borderRadius: 4, textDecoration: 'none',
                  transition: 'all 0.2s',
                  marginBottom: 8,
                }}
              >
                Upload Your Labs
              </Link>
              <Link
                to="/catalog"
                style={{
                  display: 'block', textAlign: 'center',
                  padding: '10px 28px', fontSize: 13, fontWeight: 500,
                  color: '#71717A', textDecoration: 'none',
                  transition: 'color 0.2s',
                }}
              >
                Browse All Supplements
              </Link>
            </div>

            {/* Quick facts */}
            <div style={{
              backgroundColor: '#FFFFFF', border: '1px solid #E4E4E7',
              borderRadius: 4, padding: 24, marginTop: 16,
            }}>
              <h4 style={{
                fontSize: 12, fontWeight: 600, letterSpacing: 1.5,
                textTransform: 'uppercase', color: '#A1A1AA',
                margin: '0 0 16px',
              }}>
                Quick Facts
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <QuickFact label="Category" value={SUBCATEGORY_LABELS[product.subcategory] || product.subcategory} />
                <QuickFact label="Route" value={product.route} />
                <QuickFact label="Timing" value={allTimings.map((t) => TIMING_LABELS[t] || t).join(', ')} />
                <QuickFact label="Evidence" value={product.evidenceLevel} />
                <QuickFact label="Clinical Tier" value={`Tier ${product.tier}`} />
                {product.gender !== 'any' && <QuickFact label="Gender" value={product.gender} />}
                {product.requiresProvider && <QuickFact label="Provider" value="Required" />}
              </div>
            </div>

            {/* FDA Disclaimer */}
            <div style={{
              marginTop: 16, padding: 16,
              backgroundColor: '#FAFAFA', borderRadius: 4,
              border: '1px solid #F4F4F5',
            }}>
              <p style={{
                fontSize: 11, color: '#A1A1AA', lineHeight: 1.5, margin: 0,
              }}>
                These statements have not been evaluated by the Food and Drug Administration.
                This product is not intended to diagnose, treat, cure, or prevent any disease.
                Always consult your healthcare provider before starting any supplement regimen.
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        .product-detail-grid{
          display:grid;
          grid-template-columns:minmax(0,1fr) 320px;
          gap:32px;
          align-items:start;
        }
        @media(max-width:768px){
          .product-detail-grid{
            grid-template-columns:1fr;
          }
        }
      `}</style>
    </div>
  );
}

// ── Nav bar ──

function DetailNav() {
  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 50,
      backgroundColor: 'rgba(250,250,250,0.85)', backdropFilter: 'blur(20px) saturate(180%)',
      borderBottom: '1px solid #E4E4E7',
      height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 24px',
    }}>
      <Link to="/landing" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
        <div style={{
          width: 30, height: 30, borderRadius: 3,
          background: 'linear-gradient(135deg, #E11D48, #FB7185)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 700, color: 'white',
        }}>
          H
        </div>
        <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: 1.8, color: '#0F172A' }}>HEMOLOGIX</span>
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Link to="/catalog" style={{
          fontSize: 13, fontWeight: 500, color: '#52525B', textDecoration: 'none',
        }}>
          Catalog
        </Link>
        <Link to="/landing" style={{
          padding: '10px 26px', fontSize: 13, fontWeight: 600,
          backgroundColor: '#18181B', color: 'white',
          borderRadius: 4, textDecoration: 'none',
        }}>
          Upload Labs
        </Link>
      </div>
    </nav>
  );
}

// ── Section wrapper ──

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      marginBottom: 32, paddingBottom: 32,
      borderBottom: '1px solid #F4F4F5',
    }}>
      <h2 style={{
        fontFamily: "'Instrument Serif', Georgia, serif",
        fontSize: 22, fontWeight: 400, color: '#18181B',
        margin: '0 0 16px',
      }}>
        {title}
      </h2>
      {children}
    </div>
  );
}

// ── Quick Fact row ──

function QuickFact({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      paddingBottom: 10, borderBottom: '1px solid #F4F4F5',
    }}>
      <span style={{ fontSize: 13, color: '#71717A' }}>{label}</span>
      <span style={{
        fontSize: 13, fontWeight: 600, color: '#18181B',
        textTransform: 'capitalize',
      }}>
        {value}
      </span>
    </div>
  );
}

// ── Evidence Badge ──

function EvidenceBadge({ level }: { level: string }) {
  const colors: Record<string, { bg: string; fill: string }> = {
    strong: { bg: '#F0FDF4', fill: '#15803D' },
    moderate: { bg: '#FFFBEB', fill: '#B45309' },
    emerging: { bg: '#F5F3FF', fill: '#6D28D9' },
  };
  const c = colors[level] || colors.moderate;
  // Filled circles based on level
  const filled = level === 'strong' ? 3 : level === 'moderate' ? 2 : 1;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 3,
      padding: '6px 12px', borderRadius: 4,
      backgroundColor: c.bg,
    }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: 8, height: 8, borderRadius: '50%',
            backgroundColor: i < filled ? c.fill : '#D4D4D8',
            transition: 'background-color 0.2s',
          }}
        />
      ))}
    </div>
  );
}
